import { ReactNode, useEffect, useState, useRef, Fragment } from 'react';
import { IconChevronDown, IconChevronUp, IconSearch, IconSelector } from '@tabler/icons-react';
import {
  Center,
  Group,
  ScrollArea,
  Table,
  Text,
  TextInput,
  UnstyledButton,
  Pagination,
  Loader,
  Title,
  Box,
  Checkbox,
  Tooltip,
} from '@mantine/core';

import './TableSort.scss';
import { UseFormReturnType } from '@mantine/form';

export interface FormProps {
  data: any[];
  search: string;
  filters?: Record<string, any>;
  sort?: string;
  page?: number;
  pageSize?: number | string;
  total?: number;
}
export interface Column<T> {
  accessor?: keyof T;
  label: string;
  render?: (value: any, row: T, index: number) => React.ReactNode;
  width?: number | string;
  omit?: boolean;
  /**
   * Field name understood by the table's `sortAdapter` (e.g. 'name', 'status').
   * When set and a `form` prop is passed, clicking this header sorts the full,
   * server-side dataset via `form.values.sort` instead of only reordering the
   * rows already loaded for the current page.
   */
  sortKey?: string;
}

interface DynamicTableProps<T> {
  externalKey?: string;
  columns: Column<T>[];
  data: T[];
  children?: ReactNode;
  loading?: boolean;
  tableProps?: React.ComponentProps<typeof Table>;
  form?: UseFormReturnType<any>;
  withSearch?: boolean;
  searchAction?: ReactNode;
  title?: string;
  emptyComponent?: ReactNode;
}

interface ThProps {
  children: React.ReactNode;
  reversed: boolean;
  sorted: boolean;
  onSort: () => void;
  width?: number | string;
}

function Th({ children, reversed, sorted, onSort, width }: ThProps) {
  const Icon = sorted ? (reversed ? IconChevronUp : IconChevronDown) : IconSelector;
  const textRef = useRef<HTMLDivElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsTextTruncated(isOverflowing);
      }
    };

    checkTruncation();
    // Recheck when window resizes or content changes
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [children, width]);

  return (
    <Table.Th className="th-mantine" w={width}>
      <UnstyledButton onClick={onSort} className="control-mantine" style={{ width: '100%' }}>
        <Group justify="space-between" wrap="nowrap" style={{ minWidth: 0 }}>
          <Tooltip
            label={children}
            position="top"
            withArrow
            multiline
            maw={300}
            disabled={!isTextTruncated}
          >
            <Text
              ref={textRef}
              fw={500}
              fz="sm"
              truncate
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: isTextTruncated ? 'help' : 'pointer',
              }}
            >
              {children}
            </Text>
          </Tooltip>
          <Center className="icon-mantine" style={{ flexShrink: 0, marginLeft: 8 }}>
            <Icon size={16} stroke={1.5} />
          </Center>
        </Group>
      </UnstyledButton>
    </Table.Th>
  );
}

function filterData<T>(data: T[], search: string) {
  const query = search.toLowerCase().trim();
  return data.filter((item) =>
    Object.values(item).some((value) => String(value).toLowerCase().includes(query)),
  );
}

function sortData<T>(
  data: T[],
  payload: { sortBy: keyof T | null; reversed: boolean; search: string },
) {
  const { sortBy } = payload;

  if (!sortBy) {
    return filterData(data, payload.search);
  }

  return filterData(
    [...data].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      const result = String(aValue).localeCompare(String(bValue));
      return payload.reversed ? -result : result;
    }),
    payload.search,
  );
}

function MantineTable<T extends Record<string, any>>({
  externalKey = `mantine-table-${Date.now()}`,
  columns,
  data,
  children,
  tableProps,
  form,
  loading = false,
  withSearch = true,
  searchAction,
  title,
  emptyComponent = (
    <Text fw={500} ta="center">
      No se encontraron resultados
    </Text>
  ),
}: DynamicTableProps<T>) {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<keyof T | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [sortedData, setSortedData] = useState(data);
  const [selectedAll, setSelectedAll] = useState(false);

  const tableData = form ? form.values.data : data;
  const searchState = form ? form.values.search : search;

  const setSearchState = (value: string) => {
    if (form) {
      form.setFieldValue('search', value);
    } else {
      setSearch(value);
    }
  };

  const setSorting = (field: keyof T) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(tableData, { sortBy: field, reversed, search }));
  };

  const currentSort = form?.values?.sort as string | undefined;
  const [currentSortKey, currentSortDirection] = currentSort ? currentSort.split('-') : [];

  const setServerSorting = (sortKey: string) => {
    const nextDirection = sortKey === currentSortKey && currentSortDirection === 'asc' ? 'desc' : 'asc';
    form?.setFieldValue('sort', `${sortKey}-${nextDirection}`);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setSortedData(
      sortData(tableData, {
        sortBy,
        reversed: reverseSortDirection,
        search: value,
      }),
    );
  };

  const handleSelectAll = () => {
    setSelectedAll((prev) => !prev);
    if (form) {
      const updatedData = tableData.map((item: any) => ({
        ...item,
        selected: !selectedAll,
      }));
      form.setFieldValue('data', updatedData);
    } else {
      setSortedData(sortedData.map((item) => ({ ...item, selected: !selectedAll })));
    }
  };

  useEffect(() => {
    setSortedData(sortData(tableData, { sortBy, reversed: reverseSortDirection, search }));
  }, [tableData]);

  useEffect(() => {
    if (!searchAction) {
      handleSearchChange(searchState);
    }
  }, [searchState]);

  const rows = sortedData.map((row, index) => (
    <Table.Tr key={index}>
      {columns.map((col) => {
        if (col.omit) return <Fragment key={col.label} />;
        return (
          <Table.Td
            key={col.accessor?.toString() ?? col.label}
            w={col.width || (col.accessor === 'selected' ? 70 : undefined)}
          >
            {col.render
              ? col.render(col.accessor ? row[col.accessor] : null, row, index)
              : col.accessor && row[col.accessor]}
          </Table.Td>
        );
      })}
    </Table.Tr>
  ));

  return (
    <Box>
      <Title>{title}</Title>
      {children}
      <ScrollArea>
        {withSearch && (
          <Group mb="xl" align="end">
            <TextInput
              flex={1}
              label="Buscar"
              placeholder="Buscar por cualquier campo"
              leftSection={<IconSearch size={16} stroke={1.5} />}
              value={searchState}
              onChange={(event) => setSearchState(event.currentTarget.value)}
            />
            {searchAction && searchAction}
          </Group>
        )}
        <Table
          key={externalKey}
          horizontalSpacing="lg"
          verticalSpacing="xs"
          miw={700}
          layout="fixed"
          className="MantineTable"
          {...tableProps}
        >
          <Table.Thead>
            <Table.Tr>
              {columns.map((col) => {
                if (col.omit) return <Fragment key={col.label} />;
                if (col.accessor === 'selected') {
                  return (
                    <Table.Th key={String(col.accessor)} w={col.width || 70}>
                      <Checkbox
                        w={55}
                        checked={selectedAll}
                        onChange={handleSelectAll}
                        color="orange.8"
                      />
                    </Table.Th>
                  );
                } else {
                  const useServerSort = Boolean(form && col.sortKey);
                  return (
                    <Th
                      key={String(col.accessor)}
                      sorted={useServerSort ? currentSortKey === col.sortKey : sortBy === col.accessor}
                      reversed={useServerSort ? currentSortDirection === 'desc' : reverseSortDirection}
                      onSort={() =>
                        useServerSort ? setServerSorting(col.sortKey!) : setSorting(col.accessor)
                      }
                      width={col.width}
                    >
                      {col.label}
                    </Th>
                  );
                }
              })}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {!loading && rows.length > 0 ? (
              rows
            ) : (
              <Table.Tr>
                <Table.Td colSpan={columns.length}>
                  {loading ? (
                    <Center h={400}>
                      <Loader color="indigo.8" size={'xl'} />
                    </Center>
                  ) : (
                    <Box>{emptyComponent}</Box>
                  )}
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
        {!loading && form && form.values.total > 1 ? (
          <Center>
            <Pagination
              value={form.values.page}
              onChange={(page) => form.setFieldValue('page', page)}
              total={Math.ceil(form.values.total)}
            />
          </Center>
        ) : null}
      </ScrollArea>
    </Box>
  );
}

export default MantineTable;
